import { WebSocketMessage, WebSocketMessageType } from '../types/websocket';
import { getCurrentUser } from './authService';

// Global flag to control WebSocket initialization
let websocketsEnabled = false;

class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout = 1000;
  private intentionalDisconnect = false;
  private messageHandlers: Map<string, ((data: any) => void)[]> = new Map();
  private sessionId: string | null = null;
  private wsUrl: string;
  private connectionQueue: string[] = []; // Queue of session IDs waiting to connect
  
  constructor(baseUrl: string) {
    // Determine the appropriate WebSocket URL based on the environment
    const determineWsUrl = () => {
      // First check for explicit environment variable for WebSocket URL
      const envWsUrl = import.meta.env.VITE_WS_URL;
      if (envWsUrl) {
        console.log(`[WEBSOCKET-SERVICE] Using WebSocket URL from environment: ${envWsUrl}`);
        return envWsUrl;
      }
      
      // Always use the same protocol as the page
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      
      // When running in development on the same machine, use window location
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        const host = window.location.host;
        return `${protocol}//${host}`;
      }
      
      // When accessing from a different machine, use the public URL
      const publicApiUrl = import.meta.env.VITE_PUBLIC_API_URL || '';
      if (publicApiUrl) {
        // If it's a full URL, extract the hostname and port
        if (publicApiUrl.startsWith('http')) {
          const url = new URL(publicApiUrl);
          // Always use wss: if the page is loaded over https:
          return `${protocol}//${url.host}`;
        }
        // Otherwise, assume it's a hostname or IP
        return `${protocol}//${publicApiUrl}`;
      }
      
      // Fallback to window location
      const host = window.location.host;
      return `${protocol}//${host}`;
    };
    
    this.wsUrl = determineWsUrl();
    console.log(`[WEBSOCKET-SERVICE] Using base URL: ${this.wsUrl}`);
  }
  
  // Check if user is authenticated before connecting
  private checkAuthentication(): boolean {
    // First check if WebSockets are globally enabled
    if (!websocketsEnabled) {
      console.warn('[WEBSOCKET-SERVICE] WebSockets are not enabled yet. Authentication may not be complete.');
      return false;
    }
    
    const user = getCurrentUser();
    if (!user) {
      console.warn('[WEBSOCKET-SERVICE] Attempted to connect without authentication');
      return false;
    }
    return true;
  }
  
  // Enable WebSockets globally - call this after authentication is confirmed
  enableWebSockets(): void {
    console.log('[WEBSOCKET-SERVICE] WebSockets enabled globally');
    websocketsEnabled = true;
    
    // Process any queued connections
    if (this.connectionQueue.length > 0) {
      console.log(`[WEBSOCKET-SERVICE] Processing ${this.connectionQueue.length} queued connections`);
      const queue = [...this.connectionQueue];
      this.connectionQueue = [];
      
      // Connect to each queued session
      queue.forEach(sessionId => {
        console.log(`[WEBSOCKET-SERVICE] Connecting to queued session: ${sessionId}`);
        this.connect(sessionId).catch(err => {
          console.error(`[WEBSOCKET-SERVICE] Failed to connect to queued session ${sessionId}:`, err);
        });
      });
    }
  }
  
  // Disable WebSockets globally - call this after logout
  disableWebSockets(): void {
    console.log('[WEBSOCKET-SERVICE] WebSockets disabled globally');
    websocketsEnabled = false;
    
    // Disconnect any active connections
    if (this.socket) {
      this.intentionalDisconnect = true;
      this.socket.close(1000, "WebSockets disabled globally");
      this.socket = null;
    }
    
    // Clear connection queue
    this.connectionQueue = [];
  }
  
  connect(sessionId: string): Promise<void> {
    // Check if WebSockets are enabled globally
    if (!websocketsEnabled) {
      console.log(`[WEBSOCKET-SERVICE] WebSockets not enabled yet, queueing connection to session ${sessionId}`);
      // Queue the connection for when WebSockets are enabled
      if (!this.connectionQueue.includes(sessionId)) {
        this.connectionQueue.push(sessionId);
      }
      return Promise.reject(new Error('WebSockets not enabled yet'));
    }
    
    // Check authentication before connecting
    if (!this.checkAuthentication()) {
      return Promise.reject(new Error('Authentication required for WebSocket connection'));
    }
    
    this.sessionId = sessionId;
    this.intentionalDisconnect = false;
    
    return new Promise((resolve, reject) => {
      try {
        // Use the wsUrl determined in the constructor
        const url = `${this.wsUrl}/sessions/${sessionId}`;
        console.log(`[WEBSOCKET-SERVICE] Connecting to: ${url}`);
        this.socket = new WebSocket(url);
        
        this.socket.onopen = () => {
          console.log(`WebSocket connected to session ${sessionId}`);
          this.reconnectAttempts = 0;
          resolve();
        };
        
        this.socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data) as WebSocketMessage;
            this.handleMessage(message);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        this.socket.onclose = () => {
          console.log(`WebSocket disconnected from session ${sessionId}`);
          this.attemptReconnect();
        };
        
        this.socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };
      } catch (error) {
        console.error('Failed to connect WebSocket:', error);
        reject(error);
      }
    });
  }
  
  disconnect(): void {
    this.intentionalDisconnect = true;
    if (this.socket && this.socket.readyState !== WebSocket.CLOSING && this.socket.readyState !== WebSocket.CLOSED) {
      this.socket.close();
    }
    this.socket = null;
    this.sessionId = null;
  }
  
  on(type: string, handler: (data: any) => void): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type)?.push(handler);
  }
  
  off(type: string, handler: (data: any) => void): void {
    const handlers = this.messageHandlers.get(type);
    if (!handlers) return;
    
    const index = handlers.indexOf(handler);
    if (index !== -1) {
      handlers.splice(index, 1);
    }
  }
  
  send(message: any): void {
    // Check authentication before sending
    if (!this.checkAuthentication()) {
      console.error('[WEBSOCKET-SERVICE] Cannot send message - not authenticated');
      return;
    }
    
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.error('WebSocket not connected');
    }
  }
  
  private handleMessage(message: WebSocketMessage): void {
    const { type } = message;
    
    // Call all registered handlers for this message type
    const handlers = this.messageHandlers.get(type) || [];
    handlers.forEach(handler => handler(message));
    
    // Also call handlers for 'all' message types
    const allHandlers = this.messageHandlers.get('all') || [];
    allHandlers.forEach(handler => handler(message));
  }
  
  private attemptReconnect(): void {
    // Don't attempt to reconnect if WebSockets are disabled globally
    if (!websocketsEnabled) {
      console.log('[WEBSOCKET-SERVICE] Not reconnecting - WebSockets are disabled globally');
      return;
    }
    
    // Don't attempt to reconnect if the disconnect was intentional
    if (this.intentionalDisconnect || this.reconnectAttempts >= this.maxReconnectAttempts || !this.sessionId) {
      if (this.intentionalDisconnect) {
        console.log('Not reconnecting due to intentional disconnect');
      } else {
        console.error('Max reconnect attempts reached or no session ID');
      }
      return;
    }
    
    // Check authentication before reconnecting
    if (!this.checkAuthentication()) {
      console.log('Not reconnecting due to missing authentication');
      return;
    }
    
    this.reconnectAttempts++;
    const timeout = this.reconnectTimeout * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(() => {
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      this.connect(this.sessionId!).catch((err) => {
        console.error('Reconnection attempt failed:', err);
        // Connection failed, will retry on next onclose event
      });
    }, timeout);
  }
  
  // Check if WebSockets are enabled
  isEnabled(): boolean {
    return websocketsEnabled;
  }
}

// Create a singleton instance with empty base URL
// The constructor will handle determining the appropriate URL
const websocketService = new WebSocketService('');

export default websocketService;
